import { useCallback, useEffect, useRef, useState } from 'react';
import apiService, { ServiceType } from '../services/api';

/**
 * What a strategy's bot is doing on a portfolio today, read from Core's StrategyExecution —
 * the same row the server's start / stop / cash-op guards check. A local "I pressed Start"
 * flag cannot know about a schedule start, a crash, a page reload or a STOPPING that takes
 * minutes; this can.
 *
 * - `starting` STARTING · `running` PASSED · `stopping` STOPPING
 * - `stopped`  STOP / ERROR / ABANDONED, or no execution today
 * - `unknown`  not loaded yet, or the read failed — callers keep the server as the guard
 */
export type BotPhase = 'starting' | 'running' | 'stopping' | 'stopped' | 'unknown';

type ExecutionRow = { id: string; status: string; createdAt: string };

const PHASE_OF: Record<string, BotPhase> = {
  STARTING: 'starting',
  PASSED: 'running',
  STOPPING: 'stopping',
};

/** Fast while a transition is in flight, slow once settled. */
const TRANSITION_POLL_MS = 3_000;
const STEADY_POLL_MS = 15_000;

export const phaseOf = (rows: ExecutionRow[] | null | undefined): BotPhase => {
  if (!rows) return 'unknown';
  if (rows.length === 0) return 'stopped';
  const latest = [...rows].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )[0];
  return PHASE_OF[latest.status] ?? 'stopped';
};

/** `true` while anything of the bot is up — starting, running or winding down. */
export const isBotUp = (phase: BotPhase): boolean =>
  phase === 'starting' || phase === 'running' || phase === 'stopping';

export function useExecutionStatus(opts: {
  portfolioId?: string | null;
  strategyType: 'GRID' | 'PAIR_TRADING';
  enabled?: boolean;
}): { phase: BotPhase; refresh: () => Promise<void> } {
  const { portfolioId, strategyType, enabled = true } = opts;
  const [phase, setPhase] = useState<BotPhase>('unknown');
  // Only the newest request may write: a slow poll must not overwrite a fresh refresh.
  const seqRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!portfolioId) {
      setPhase('unknown');
      return;
    }
    const seq = ++seqRef.current;
    try {
      const res = await apiService.getService(ServiceType.CORE).get<ExecutionRow[]>(
        'bot/executions',
        { params: { portfolioId, strategyType } },
      );
      if (seq === seqRef.current) setPhase(phaseOf(Array.isArray(res.data) ? res.data : []));
    } catch {
      // Keep the last known phase: a blip must not flip the buttons.
    }
  }, [portfolioId, strategyType]);

  useEffect(() => {
    setPhase('unknown');
    if (!enabled) return;
    void refresh();
  }, [refresh, enabled]);

  const transitioning = phase === 'starting' || phase === 'stopping';
  useEffect(() => {
    if (!enabled || !portfolioId) return;
    const id = window.setInterval(
      () => { void refresh(); },
      transitioning ? TRANSITION_POLL_MS : STEADY_POLL_MS,
    );
    return () => window.clearInterval(id);
  }, [enabled, portfolioId, refresh, transitioning]);

  return { phase, refresh };
}
